import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { socket } from "../api";
import { parseJWT } from "../utils/jwtUtils";
import cardBackImg from "../assets/Carta-card-bg2-removebg-preview.png";
import tableBg from "../assets/Game-background1.jpg";

/* ================= CARD BACK ================= */

const CardBack = () => (
  <img
    src={cardBackImg}
    alt="back"
    className="w-24 h-36 rounded-lg shadow-xl"
  />
);

/* ================= CARD FRONT ================= */

const CardFront = ({ card, small }) => {
  if (!card) return null;

  const isRed = card.suit === "♥" || card.suit === "♦";

  return (
    <div
      className={`${
        small ? "w-16 h-24 text-xs" : "w-24 h-36 text-sm"
      } bg-white rounded-lg shadow-xl flex flex-col justify-between p-2`}
    >
      <div
        className={`font-bold ${isRed ? "text-red-600" : "text-black"}`}
        style={{
          fontFamily:
            "Apple Color Emoji, Segoe UI Symbol, Noto Sans Symbols, sans-serif",
        }}
      >
        {card.rank}
        <br />
        {card.suit}
      </div>

      <div
        className={`text-4xl text-center opacity-20 ${
          isRed ? "text-red-400" : "text-black"
        }`}
      >
        {card.suit}
      </div>

      <div
        className={`font-bold rotate-180 ${isRed ? "text-red-600" : "text-black"}`}
      >
        {card.rank}
        <br />
        {card.suit}
      </div>
    </div>
  );
};

/* ================= MAIN ================= */

export default function LatLatGames() {
  const { roomId } = useParams();
  const [game, setGame] = useState(null);
  const [result, setResult] = useState(null);
  const [lastDrawnCard, setLastDrawnCard] = useState(null);
  const [timer, setTimer] = useState(30);
  const joinedRef = useRef(false);

  let phone = null;
  let name = null;

  try {
    const stored = localStorage.getItem("userInfo");
    if (stored) {
      const parsed = JSON.parse(stored);
      const userInfo = parseJWT(parsed?.token);
      phone = userInfo?.phone;
      name = userInfo?.username;
    }
  } catch {}

  /* ================= SOCKET ================= */

  useEffect(() => {
    socket.on("joined-room", setGame);
    socket.on("game-started", setGame);

    socket.on("game-update", (data) => {
      setGame(data);
    });

    socket.on("card-drawn", (data) => {
      setLastDrawnCard(data.card);
    });

    socket.on("game-over", (data) => {
      // show final card first
      if (data?.finalCard) {
        setLastDrawnCard(data.finalCard);
      }

      // delay result popup
      setTimeout(() => {
        setResult(data?.winner?.phone === phone ? "WIN" : "LOSE");
      }, 2000); // 2 seconds delay
    });

    return () => {
      socket.off("joined-room");
      socket.off("game-started");
      socket.off("game-update");
      socket.off("card-drawn");
      socket.off("game-over");
    };
  }, [phone]);

  useEffect(() => {
    if (!roomId || !phone) return;

    if (!joinedRef.current) {
      joinedRef.current = true;

      socket.emit("join-carta-room", {
        roomId,
        phone,
        name,
      });
    }
  }, [roomId, phone, name]);

  useEffect(() => {
    if (!roomId || !phone) return;

    const handleReconnect = () => {
      //  console.log("🔁 Socket reconnected — rejoining room");

      socket.emit("join-carta-room", {
        roomId,
        phone,
        name,
      });
    };

    // run if socket reconnects
    socket.on("connect", handleReconnect);

    return () => {
      socket.off("connect", handleReconnect);
    };
  }, [roomId, phone, name]);

  useEffect(() => {
    if (!game || typeof game.currentTurn !== "number") return;
    // console.log("⏳ Turn changed:", game.currentTurn);

    setTimer(30);

    const interval = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          // console.log("⏰ Auto draw triggered");
          socket.emit("draw-card", { roomId });
          return 30;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [game?.currentTurn, roomId]);

  if (!game) {
    return <div className="text-white p-10">Loading Game...</div>;
  }

  const players = game.players || [];

  const me = players.find((p) => p.phone === phone);
  const opponent = players.find((p) => p.phone !== phone);

  let isMyTurn = false;

  if (!game.firstCard) {
    isMyTurn =
      game.currentTurn === socket.id ||
      players.find((p) => p.id === game.currentTurn)?.phone === phone;
  } else {
    isMyTurn =
      game.currentTurn === socket.id ||
      players.find((p) => p.id === game.currentTurn)?.phone === phone;
  }

  /* ================= RULE SELECT ================= */

  if (!game.rule) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center text-white"
        style={{
          backgroundImage: `url(${tableBg})`,
          backgroundSize: "cover",
        }}
      >
        <h1 className="text-2xl font-bold mb-6">
          {isMyTurn ? "የጨዋታ መመሪያ አስቀምጥ " : "የዚህን ዙር የጨዋታ ህግ እየመረጠ ነው..."}
        </h1>

        {isMyTurn && (
          <div className="flex gap-6">
            <button
              onClick={() => socket.emit("set-rule", { roomId, rule: "under" })}
              className="bg-blue-600 px-6 py-3 rounded-xl"
            >
              ዝቅ
            </button>

            <button
              onClick={() => socket.emit("set-rule", { roomId, rule: "over" })}
              className="bg-green-600 px-6 py-3 rounded-xl"
            >
              ከፍ
            </button>
          </div>
        )}
      </div>
    );
  }

  /* ================= FIRST CARD PICK ================= */

  if (game.rule && !game.firstCard) {
    const deck = Array.from({ length: 52 }, (_, i) => i);

    const columns = [
      deck.slice(0, 13),
      deck.slice(13, 26),
      deck.slice(26, 39),
      deck.slice(39, 52),
    ];

    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center text-white px-4"
        style={{
          backgroundImage: `url(${tableBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <h1 className="text-xl sm:text-2xl font-bold mb-10 text-center">
          {isMyTurn ? "Choose Any Card" : "Waiting First Player..."}
        </h1>

        {/* 4 Card Columns */}
        <div className="grid grid-cols-4 w-full max-w-6xl justify-items-center">
          {columns.map((column, colIndex) => (
            <div key={colIndex} className="relative flex flex-col items-center">
              {column.map((cardIndex, i) => (
                <div
                  key={cardIndex}
                  onClick={() => {
                    if (!isMyTurn) return;

                    socket.emit("pick-first-card", {
                      roomId,
                      cardIndex,
                    });
                  }}
                  className={`transition-transform duration-200 ${
                    isMyTurn
                      ? "cursor-pointer hover:-translate-y-2 hover:scale-105"
                      : "opacity-50"
                  }`}
                  style={{
                    marginTop: i === 0 ? 0 : "-120px",
                    zIndex: i,
                  }}
                >
                  <CardBack />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ================= PLAY MODE ================= */

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center text-white"
      style={{
        backgroundImage: `url(${tableBg})`,
        backgroundSize: "cover",
      }}
    >
      <h1
        className="text-4xl font-extrabold mb-4 tracking-widest
bg-gradient-to-r from-yellow-300 via-orange-400 to-yellow-500
text-transparent bg-clip-text
drop-shadow-[0_0_12px_rgba(255,200,0,0.8)]
animate-pulse"
      >
        ላጥ ላጥ
      </h1>

      <div
        className="
 px-6 rounded-xl
bg-white/10 backdrop-blur-md
border border-white/20
shadow-[0_0_15px_rgba(0,0,0,0.5)]
text-lg font-semibold tracking-wide
"
      >
        {isMyTurn ? (
          <span className="text-green-400">
            {(me?.name || "P1").slice(0, 3)}** Turn
          </span>
        ) : (
          <span className="text-yellow-300">
            {(opponent?.name || "P2").slice(0, 3)}** Turn
          </span>
        )}
      </div>

      {/* Timer */}
      <div className="flex flex-col items-center mt-6">
        <div className="w-64 h-12 bg-black rounded-2xl flex items-center px-4 shadow-xl">
          <div
            className="h-4 bg-green-500 rounded-full transition-all duration-1000"
            style={{ width: `${(timer / 30) * 100}%` }}
          />
        </div>

        <p className="text-white text-xl font-semibold mt-2">{timer}s</p>
      </div>

      <div className="flex items-center gap-20 mb-6">
        {lastDrawnCard && (
          <div className="mb-6 animate-pulse">
            <p
              className="
mb-3
px-1 py-1
rounded-lg
bg-black/50
border border-white/20
shadow-[0_0_10px_rgba(0,0,0,0.7)]
text-lg font-semibold
tracking-wide
text-cyan-300
"
            >
              Last Drawn Card
            </p>
            <CardFront card={lastDrawnCard} />
          </div>
        )}

        <div
          onClick={() => {
            if (!isMyTurn) return;
            socket.emit("draw-card", { roomId });
          }}
          className={`relative ${isMyTurn ? "cursor-pointer" : "opacity-40"}`}
        >
          <CardBack />
          <div className="absolute inset-0 flex items-center justify-center text-2xl font-bold">
            {game.deckCount}
          </div>
        </div>
      </div>

      <CardFront card={game.firstCard} />

      {(game.rule || game.targetRank) && (
        <div
          className="
    my-4 px-6 py-3 rounded-xl
    bg-white/10 backdrop-blur-md
    border border-white/20
    shadow-[0_0_15px_rgba(0,0,0,0.5)]
    text-lg font-semibold tracking-wide
    flex gap-6 items-center justify-center
  "
        >
          {game.rule && (
            <div className="text-yellow-300">
              Rule :{" "}
              <span className="font-bold">{game.rule.toUpperCase()}</span>
            </div>
          )}

          {game.targetRank && (
            <div className="text-cyan-300">
              Target Rank :
              <span className="font-bold ml-1 text-xl">{game.targetRank}</span>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-900 to-black text-white px-8 py-10 rounded-2xl shadow-2xl text-center">
            <div className="text-6xl mb-4">
              {result === "WIN" ? "🏆" : "💀"}
            </div>

            <h1 className="text-3xl font-bold mb-2">
              {result === "WIN" ? "አሸንፈዋል!" : "ለጊዜው ተሸንፈዋል"}
            </h1>

            <p className="text-gray-400 mb-6">
              {result === "WIN"
                ? "እሄንን እድል ለመድገም ድጋሚ ይጫወቱ"
                : "በሚቀጥለው ዙር የተሻለ ዕድል ይገጥምዎታል"}
            </p>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => (window.location.href = "/latlat-games")}
                className="bg-blue-600 hover:bg-blue-500 px-6 py-3 rounded-xl"
              >
                ወደ ቀጣይ ጨዋታ
              </button>

              {result === "LOSE" && (
                <button
                  onClick={() => (window.location.href = "/latlat-games")}
                  className="bg-red-600 hover:bg-red-500 px-6 py-3 rounded-xl"
                >
                  Play Again
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
